import type {
  Category,
  CategoryTreeItem,
  CreateCategoryPayload,
  GetCategoriesPayload,
  UpdateCategoryPayload,
} from '../../types/index.js';
import { baseApi } from '../base-api.js';

type CategoriesResponse = { categories: Category[] };
type CategoryTreeResponse = { tree: CategoryTreeItem[] };
type CategoryResponse = { category: Category };

const categoryListTags = (result: Category[] | undefined) =>
  result
    ? [...result.map(({ id }) => ({ type: 'Category' as const, id })), { type: 'Category' as const, id: 'LIST' }]
    : [{ type: 'Category' as const, id: 'LIST' }];

/**
 * Mirrors `apps/server/src/modules/category` (`categoryController`, prefix `/categories`).
 *
 * `Category`'s own shape embeds live `module`/`parent` snippets (see `module.api.ts`'s doc
 * comment for the module direction) and the tree can be nested arbitrarily deep — rather than
 * track exactly which ancestor/descendant ids are affected by a rename or reparent, every
 * mutation here invalidates the whole `Category` `LIST` tag, which also covers
 * `getCategoriesTree` (same tag). `Question.category` and `Recommendation.category` are also
 * live-embedded (`{ id, label, ... }`, see `question.api.ts`/`recommendation.api.ts`), so any
 * mutation invalidates `Question` `LIST` and `Recommendation` `LIST` too. Create/delete
 * additionally invalidate `Module` `LIST` since `categories_count` is counted live — update does
 * not, since renaming/moving a category never changes the count.
 */
export const categoryApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCategories: builder.query<Category[], GetCategoriesPayload>({
      query: (payload) => ({ path: ['categories'], method: 'get', query: payload }),
      transformResponse: (response: CategoriesResponse) => response.categories,
      providesTags: (result) => categoryListTags(result),
    }),

    getCategoriesTree: builder.query<CategoryTreeItem[], string>({
      query: (moduleId) => ({ path: ['categories', 'tree', { param: 'module_id', value: moduleId }], method: 'get' }),
      transformResponse: (response: CategoryTreeResponse) => response.tree,
      providesTags: [{ type: 'Category', id: 'LIST' }],
    }),

    getCategoryById: builder.query<Category, string>({
      query: (id) => ({ path: ['categories', { param: 'category_id', value: id }], method: 'get' }),
      transformResponse: (response: CategoryResponse) => response.category,
      providesTags: (_result, _error, id) => [{ type: 'Category', id }],
    }),

    createCategory: builder.mutation<Category, CreateCategoryPayload>({
      query: (body) => ({ path: ['categories'], method: 'post', body }),
      transformResponse: (response: CategoryResponse) => response.category,
      invalidatesTags: [
        { type: 'Category', id: 'LIST' },
        { type: 'Module', id: 'LIST' },
      ],
    }),

    updateCategory: builder.mutation<Category, { id: string; payload: UpdateCategoryPayload }>({
      query: ({ id, payload }) => ({
        path: ['categories', { param: 'category_id', value: id }],
        method: 'put',
        body: payload,
      }),
      transformResponse: (response: CategoryResponse) => response.category,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Category', id },
        { type: 'Category', id: 'LIST' },
        { type: 'Question', id: 'LIST' },
        { type: 'Recommendation', id: 'LIST' },
      ],
    }),

    deleteCategory: builder.mutation<void, string>({
      query: (id) => ({ path: ['categories', { param: 'category_id', value: id }], method: 'delete' }),
      transformResponse: (): void => undefined,
      invalidatesTags: (_result, _error, id) => [
        { type: 'Category', id },
        { type: 'Category', id: 'LIST' },
        { type: 'Module', id: 'LIST' },
        { type: 'Question', id: 'LIST' },
        { type: 'Recommendation', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetCategoriesQuery,
  useGetCategoriesTreeQuery,
  useGetCategoryByIdQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
} = categoryApi;
